import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { paywallFlowStore } from '@/stores/PaywallFlowStore';
import { isApplePayCancelledError, useTrialPayment } from '@/hooks/useTrialPayment';
import { sendLoadSuccess, sendPaymentFailed } from '@/hooks/useWebViewMessaging';
import { PaywallHero } from '@/components/paywall/PaywallHero';
import { PlansSection } from '@/components/paywall/PlansSection';
import { CtaButton } from '@/components/ui/CtaButton';
import { PaymentError } from '@/components/ui/PaymentError';
import { NoPay } from '@/components/paywall/NoPay';
import { V1Desc } from '@/components/paywall/V1Desc';
import { RestoreSubscription } from '@/components/paywall/RestoreSubscription';
import { LegalLinks } from '@/components/paywall/LegalLinks';
import { SecurityAnalysisModal } from '@/components/ui/SecurityAnalysisModal';
import { useSecurityAnalysisModal } from '@/hooks/useSecurityAnalysisModal';
import { syncPaywallMetaFromUrl } from '@/utils/paywallUrlParams';

const Screen = styled.div`
  max-width: 430px;
  margin: 0 auto;
  padding:
    calc(16px + var(--app-safe-area-top))
    calc(16px + var(--app-safe-area-right))
    calc(16px + var(--app-safe-area-bottom))
    calc(16px + var(--app-safe-area-left));
`;

const CtaSection = styled.section`
  margin-bottom: 12px;
  margin-top: 20px;
`;

export const PaywallV1 = observer(() => {
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(true);
  const applePayLoadStatusReportedRef = useRef(false);

  const isRestore = searchParams.get('restore') === 'true';

  // Security analysis modal - don't show if restore=true
  const { isOpen: isModalOpen, closeModal } = useSecurityAnalysisModal();

  const {
    initializeApplePay,
    showApplePay,
    preferredPaymentMethod,
    isInitializing,
    isApplePayReady,
    trialPlan,
    trialPlans,
    selectTrialPlan,
    refreshPaymentRequest,
    handlePaymentError,
  } = useTrialPayment();

  // Load plans and initialize Apple Pay on component mount
  useEffect(() => {
    const init = async () => {
      if (!subscriptionStore.plans || subscriptionStore.plans.length === 0) {
        await subscriptionStore.fetchPlans();
      }

      // Initialize Apple Pay after plans are loaded
      const applePayReady = await initializeApplePay();

      // Show error if Apple Pay is not available
      if (!applePayReady) {
        const errorMsg = 'Apple Pay is not available on this device. Please use Safari on iOS or macOS.';
        setError(errorMsg);
      }

      if (!applePayLoadStatusReportedRef.current) {
        applePayLoadStatusReportedRef.current = true;
        if (applePayReady) {
          sendLoadSuccess();
        }
        else {
          sendPaymentFailed('Apple Pay is not available on this device. Please use Safari on iOS or macOS.');
        }
      }

      // Page loading is complete
      setIsPageLoading(false);

      syncPaywallMetaFromUrl(searchParams);
    };

    void init();
  }, [initializeApplePay, searchParams]);

  useEffect(() => {
    if (!trialPlan?.planId) {
      return;
    }

    void refreshPaymentRequest();
  }, [trialPlan?.planId, refreshPaymentRequest]);

  const onCta = () => {
    if (!trialPlan) {
      const errorMsg = 'Trial plan is not available. Please refresh the page.';
      setError(errorMsg);
      sendPaymentFailed(errorMsg);
      return;
    }

    if (processing || isInitializing || !isApplePayReady) return;

    // Check if Apple Pay is available
    if (!preferredPaymentMethod) {
      const errorMsg = 'Apple Pay is not available on this device. Please use Safari on iOS or macOS.';
      setError(errorMsg);
      sendPaymentFailed(errorMsg);
      return;
    }

    setError('');
    setProcessing(true);

    showApplePay(paywallFlowStore.meta.trim() || undefined).catch((err: unknown) => {
      if (isApplePayCancelledError(err)) {
        return;
      }

      console.error('Payment process failed:', err);
      setError(handlePaymentError(err));
    }).finally(() => {
      setProcessing(false);
    });
  };

  return (
    <>
      <Screen>
        <PaywallHero />
        {trialPlans.length > 0 && (
          <PlansSection
            plans={trialPlans}
            selectedPlanId={trialPlan?.planId ?? null}
            onSelect={selectTrialPlan}
          >
            Free Trial Enabled
          </PlansSection>
        )}
        <CtaSection>
          <CtaButton
            showArrow={!isPageLoading && !isInitializing && isApplePayReady}
            loading={processing || isInitializing || isPageLoading}
            disabled={processing || isInitializing || !isApplePayReady || isPageLoading}
            onClick={onCta}
          >
            Try For Free
          </CtaButton>
          <PaymentError error={error} />
        </CtaSection>
        <NoPay trialEnabled={Boolean(trialPlan?.trial)} />
        <V1Desc />
        <RestoreSubscription />
        <LegalLinks termsHref="/policy/terms" privacyHref="/policy/privacy" />
      </Screen>

      {!isRestore && <SecurityAnalysisModal isOpen={isModalOpen} onClose={closeModal} />}
    </>
  );
});
